import { useState, useRef, useEffect, useCallback } from "react";
import { CheckCircle2, MapPin, AlertCircle, AlertTriangle, RotateCcw, ScanLine, Lock } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useActiveStore } from "@/hooks/use-active-store";
import { useLookupOrder, useRecordPick, usePickPackSettings } from "@/hooks/use-fulfillment";
import { PickPackSettingsDialog } from "@/components/fulfillment/pick-pack-settings-dialog";
import { cn } from "@/lib/utils";
import { playSuccess, playError, playComplete } from "@/lib/sounds";
import {
  getScannedCount,
  incrementScannedItem,
  setScannedItemOverride,
  type ScannedItem,
} from "@/lib/fulfillment/scan-state";
import { isolateScannerKeys } from "@/hooks/use-scanner-input";
import { MetaLookupHeader, MetaSourcesLine } from "@/components/meta-orders/meta-badge";
import type { MetaLookup } from "@/services/fulfillment";

/** Poruka servera kad je namjerna i na bosanskom (npr. „Narudžba VP-… je otkazana“, „Nemate
 *  pristup …“); tehničke greške WooCommercea (engleski, kodovi) ostaju iza opšte poruke. */
function lookupErrorMessage(err: unknown): string {
  const message = err instanceof Error ? err.message : "";
  return /^(Narudžba|Nemate pristup)/.test(message) ? message : "Narudžba nije pronađena";
}

export function PickPage() {
  const { storeId } = useActiveStore();
  const recordPick = useRecordPick();
  const { data: displaySettings } = usePickPackSettings(storeId);
  const imgSize = displaySettings?.imageSize ?? 40;
  const locFontSize = displaySettings?.locationFontSize ?? 12;
  const hideWarehouseName = displaySettings?.hideWarehouseName ?? false;

  const [orderInput, setOrderInput] = useState("");
  const [activeOrderQuery, setActiveOrderQuery] = useState("");
  const [skuInput, setSkuInput] = useState("");
  const [scannedItems, setScannedItems] = useState<ScannedItem[]>([]);
  const [showAlternates, setShowAlternates] = useState<string | null>(null);

  const [completionError, setCompletionError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const requestIdRef = useRef<string | null>(null);
  const pickInFlightRef = useRef(false);
  const orderInputRef = useRef<HTMLInputElement>(null);
  const skuInputRef = useRef<HTMLInputElement>(null);

  const { data: lookupData, isLoading, error: lookupError } = useLookupOrder(storeId, activeOrderQuery);

  const order = lookupData?.order;
  const pickItems = lookupData?.pickItems;
  const orderId = lookupData?.orderId;
  // See pack.tsx — use resolved storeId (from lookup) so tracking-scanned orders
  // from a different shop than the sidebar don't get recorded under the wrong store.
  const resolvedStoreId: string | undefined = lookupData?.storeId;
  // Meta narudžba (M-12): zbirna lista svih članova; POST ide s orderId "M-12" kao i obična.
  const meta: MetaLookup | null = lookupData?.meta ?? null;
  const totalNeeded = pickItems?.reduce((sum: number, item: any) => sum + item.quantity, 0) ?? 0;
  const totalScanned = scannedItems.reduce((sum, s) => sum + s.scannedCount, 0);
  const allDone = totalNeeded > 0 && totalScanned >= totalNeeded;
  const progressPct = totalNeeded > 0 ? Math.round((totalScanned / totalNeeded) * 100) : 0;

  useEffect(() => {
    orderInputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (lookupError && activeOrderQuery) {
      playError();
      toast.error(lookupErrorMessage(lookupError));
      setActiveOrderQuery("");
      setOrderInput("");
      setTimeout(() => orderInputRef.current?.focus(), 100);
    }
  }, [lookupError]);

  useEffect(() => {
    if (pickItems && !order) return;
    if (pickItems && skuInputRef.current) {
      skuInputRef.current.focus();
    }
  }, [pickItems]);

  const resetState = useCallback(() => {
    setActiveOrderQuery("");
    setOrderInput("");
    setSkuInput("");
    setScannedItems([]);
    setShowAlternates(null);
    setCompletionError(null);
    setCompleted(false);
    requestIdRef.current = null;
    pickInFlightRef.current = false;
    setTimeout(() => orderInputRef.current?.focus(), 100);
  }, []);

  const doRecordPick = useCallback(() => {
    if (!allDone || !orderId || !resolvedStoreId || completed || pickInFlightRef.current) return;
    pickInFlightRef.current = true;
    requestIdRef.current ??= crypto.randomUUID();
    setCompletionError(null);
    recordPick.mutate({
      orderId, storeId: resolvedStoreId, itemsJson: JSON.stringify(scannedItems),
      requestId: requestIdRef.current,
    }, {
      onSuccess: () => {
        setCompleted(true);
        playComplete();
        toast.success("Priprema je spremljena");
      },
      onError: (error) => {
        setCompletionError(error.message || "Greška pri spremanju pripreme");
        playError();
      },
      onSettled: () => { pickInFlightRef.current = false; },
    });
  }, [allDone, orderId, resolvedStoreId, completed, recordPick, scannedItems]);

  useEffect(() => {
    if (allDone && !completionError && !completed) doRecordPick();
  }, [allDone, completionError, completed, doRecordPick]);

  useEffect(() => {
    if (!completed) return;
    const timer = setTimeout(resetState, 2000);
    return () => clearTimeout(timer);
  }, [completed, resetState]);

  function handleOrderSubmit(e: React.KeyboardEvent) {
    if (e.key === "Enter" && orderInput.trim()) {
      e.preventDefault();
      setActiveOrderQuery(orderInput.trim());
      setScannedItems([]);
    }
  }

  function handleSkuScan(e: React.KeyboardEvent) {
    if (e.key !== "Enter" || !skuInput.trim() || !pickItems) return;
    e.preventDefault();

    const sku = skuInput.trim();
    setSkuInput("");

    const item = pickItems.find((p: any) => p.sku === sku);
    if (!item) {
      playError();
      toast.error("SKU nije u narudžbi");
      return;
    }

    const currentCount = getScannedCount(scannedItems, sku);

    if (currentCount >= item.quantity) {
      playError();
      toast.error(`Već skenirano ${item.quantity}x ${sku}`);
      return;
    }

    setScannedItems((prev) => incrementScannedItem(prev, sku));

    playSuccess();
    toast.success(`${sku} skeniran`);
  }

  function handleItemTap(sku: string, quantity: number) {
    const currentCount = getScannedCount(scannedItems, sku);

    if (currentCount >= quantity) return;

    setScannedItems((prev) => incrementScannedItem(prev, sku));

    playSuccess();
  }

  function handleOverride(sku: string, binLabel: string) {
    setScannedItems((prev) => setScannedItemOverride(prev, sku, binLabel));
    setShowAlternates(null);
    toast.info(`Lokacija promijenjena na ${binLabel}`);
  }

  if (allDone) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 p-4">
        <div className="ring-1 ring-green-600/30 bg-green-600/5 p-8 flex flex-col items-center gap-3">
          {completed && <CheckCircle2 className="h-10 w-10 text-green-600" />}
          <p className="text-sm font-medium">
            {completed
              ? meta
                ? `Spojena pošiljka ${meta.number} pripremljena`
                : `Narudžba #${order?.number || orderId} pripremljena`
              : "Spremanje pripreme"}
          </p>
          {completionError ? (
            <>
              <p className="text-xs text-destructive">{completionError}</p>
              <Button onClick={doRecordPick} disabled={recordPick.isPending}>Pokušaj ponovo</Button>
              <Button variant="outline" onClick={resetState} disabled={recordPick.isPending}>Ponovo učitaj narudžbu</Button>
            </>
          ) : (
            <p className="text-xs text-muted-foreground">
              {completed ? "Priprema za sljedeću narudžbu..." : "Čekanje potvrde servera…"}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-t-2 border-t-sky-500 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="bg-sky-500/15 text-sky-600 dark:text-sky-400 px-2 py-0.5 text-xs font-bold tracking-wider">PICK</span>
            <span className="text-xs text-muted-foreground">priprema — ne završava narudžbu</span>
          </div>
          <PickPackSettingsDialog />
        </div>
        {!pickItems ? (
          <div className="relative">
            <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              ref={orderInputRef}
              value={orderInput}
              onChange={(e) => setOrderInput(e.target.value)}
              onKeyDown={isolateScannerKeys(handleOrderSubmit)}
              placeholder="Skeniraj narudžbu, VP broj ili tracking broj"
              className="h-11 pl-9 text-sm"
              autoComplete="off"
            />
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                ref={skuInputRef}
                value={skuInput}
                onChange={(e) => setSkuInput(e.target.value)}
                onKeyDown={isolateScannerKeys(handleSkuScan)}
                placeholder="Skeniraj artikal (SKU)"
                className="h-11 pl-9 text-sm flex-1"
                autoComplete="off"
              />
            </div>
            <Button variant="ghost" size="icon" onClick={resetState} className="shrink-0 h-11 w-11">
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        )}
        {isLoading && <p className="text-xs text-muted-foreground">Učitavanje...</p>}
      </div>

      {pickItems && order && (
        <div className="flex-1 overflow-auto">
          <div className="px-4 py-3 border-b bg-muted/30 flex flex-col gap-2">
            <div className="flex items-start justify-between gap-2">
              {meta ? (
                <MetaLookupHeader meta={meta} />
              ) : (
                <span className="text-sm font-medium">#{order.number || orderId}</span>
              )}
              <span className="text-xs tabular-nums font-medium shrink-0">{totalScanned}/{totalNeeded}</span>
            </div>
            {(order.shipping?.first_name || order.shipping?.last_name) && (
              <span className="text-base font-semibold">
                {order.shipping?.first_name} {order.shipping?.last_name}
              </span>
            )}
            <div className="h-1 bg-muted w-full">
              <div
                className="h-full bg-foreground transition-all duration-300"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            {order.meta_data?.find((m: any) => m.key === "_greenbox_important_note")?.value && (
              <div className="flex items-start gap-1.5 bg-red-500/10 px-2.5 py-1.5 text-xs">
                <AlertTriangle className="h-3 w-3 text-red-600 shrink-0 mt-0.5" />
                <span className="whitespace-pre-wrap font-medium text-red-800">{order.meta_data.find((m: any) => m.key === "_greenbox_important_note").value}</span>
              </div>
            )}
            {order.meta_data?.find((m: any) => m.key === "_komoran_internal_note")?.value && (
              <div className="flex items-start gap-1.5 bg-yellow-500/10 px-2.5 py-1.5 text-xs">
                <Lock className="h-3 w-3 text-yellow-600 shrink-0 mt-0.5" />
                <span className="whitespace-pre-wrap">{order.meta_data.find((m: any) => m.key === "_komoran_internal_note").value}</span>
              </div>
            )}
          </div>

          <div className="flex flex-col">
            {pickItems.map((item: any, idx: number) => {
              const scanned = scannedItems.find((s) => s.sku === item.sku);
              const scannedCount = scanned?.scannedCount ?? 0;
              const isDone = scannedCount >= item.quantity;
              const hasAlternates = item.alternateLocations?.length > 0;
              const overrideBin = scanned?.overrideBin;

              return (
                <div
                  key={idx}
                  className={cn(
                    "flex flex-col border-b px-4 py-3 transition-colors",
                    isDone && "bg-green-600/5"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => handleItemTap(item.sku, item.quantity)}
                      disabled={isDone}
                      className="shrink-0 w-7 h-7 flex items-center justify-center -m-1 active:scale-90 transition-transform disabled:pointer-events-none"
                    >
                      {isDone ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      ) : overrideBin ? (
                        <AlertCircle className="h-4 w-4 text-yellow-500" />
                      ) : (
                        <div className="h-3 w-3 ring-2 ring-foreground/20" />
                      )}
                    </button>
                    {item.image && imgSize > 0 && (
                      <img
                        src={item.image}
                        alt={item.sku}
                        className="shrink-0 object-cover ring-1 ring-foreground/10"
                        style={{ width: imgSize, height: imgSize }}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs font-medium truncate">{item.sku}</span>
                        <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                          {scannedCount > 0 ? `${scannedCount}/${item.quantity}` : `×${item.quantity}`}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{item.name}</p>
                      <MetaSourcesLine sources={item.sources} />
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 mt-1.5">
                    <MapPin className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span className="font-medium tabular-nums break-words min-w-0" style={{ fontSize: locFontSize }}>
                      {overrideBin || (hideWarehouseName ? item.binLabel : `${item.warehouseName} → ${item.binLabel}`)}
                    </span>
                    {hasAlternates && !isDone && (
                      <button
                        onClick={() => setShowAlternates(showAlternates === item.sku ? null : item.sku)}
                        className="text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground px-1 shrink-0"
                      >
                        alt
                      </button>
                    )}
                  </div>

                  {showAlternates === item.sku && (
                    <div className="ml-[18px] mt-1.5 flex flex-col gap-0.5">
                      {item.alternateLocations.map((alt: any, ai: number) => (
                        <button
                          key={ai}
                          onClick={() => handleOverride(item.sku, hideWarehouseName ? alt.binLabel : `${alt.warehouseName} → ${alt.binLabel}`)}
                          className="text-xs text-left px-2 py-1.5 hover:bg-muted transition-colors"
                        >
                          {hideWarehouseName ? alt.binLabel : `${alt.warehouseName} → ${alt.binLabel}`}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
